import React from "react";
import { createPortal } from "react-dom";

export type FloatingLayerPlacement = "auto" | "bottom-start" | "top-start";

interface FloatingLayerPosition {
    top: number;
    left: number;
    width: number;
    availableHeight: number;
    resolvedPlacement: Exclude<FloatingLayerPlacement, "auto">;
}

interface FloatingLayerContextValue {
    portalContainer: Element | DocumentFragment;
    baseZIndex: number;
    registerBackHandler?: (handler: () => boolean) => () => void;
}

type FloatingLayerStyle = React.CSSProperties & {
    "--floating-layer-available-height": string;
    "--floating-layer-trigger-width": string;
};

const DEFAULT_FLOATING_LAYER_Z_INDEX = 1310;
const FLOATING_LAYER_OFFSET = 4;
const FLOATING_LAYER_VIEWPORT_PADDING = 8;

const FloatingLayerContext = React.createContext<FloatingLayerContextValue | null>(null);

/** Пропсы {@link FloatingLayerProvider}. */
export interface FloatingLayerProviderProps {
    /** Portal-контейнер, общий с app overlay host. */
    portalContainer?: Element | DocumentFragment;
    /** Базовый z-index transient-слоёв поверх app window/dialog content. */
    baseZIndex?: number;
    /**
     * Опциональная интеграция с внешним back/dismiss dispatcher. \
     * Shared layer остаётся переносимым: без registrar работает только controlled `open/onOpenChange`, Escape и outside click.
     */
    registerBackHandler?: (handler: () => boolean) => () => void;
    children?: React.ReactNode;
}

/**
 * Shared host для transient UI-слоёв: dropdown, popover, listbox.
 *
 * Это не замена app-level `window`/`dialog`: floating layer не владеет бизнес-сценарием,
 * не участвует в owner cleanup и не должен использоваться для форм/окон. Его задача —
 * дать shared-компонентам единый portal container, z-index и dismiss-поведение без
 * прямой зависимости от `@app/overlay`.
 */
export function FloatingLayerProvider({
    portalContainer = document.body,
    baseZIndex = DEFAULT_FLOATING_LAYER_Z_INDEX,
    registerBackHandler,
    children,
}: FloatingLayerProviderProps) {
    const value = React.useMemo(
        () => ({
            portalContainer,
            baseZIndex,
            registerBackHandler,
        }),
        [baseZIndex, portalContainer, registerBackHandler],
    );

    return <FloatingLayerContext.Provider value={value}>{children}</FloatingLayerContext.Provider>;
}

/** Пропсы {@link FloatingLayer}. */
export interface FloatingLayerProps {
    /** DOM anchor, относительно которого позиционируется transient layer. */
    anchorRef: React.RefObject<HTMLElement | null>;
    open: boolean;
    /**
     * Где открыть слой относительно anchor.
     * `auto` выбирает сторону с большим доступным местом и ограничивает высоту viewport.
     * @default "auto"
     */
    placement?: FloatingLayerPlacement;
    className?: string;
    children?: React.ReactNode;
    onOpenChange?: (open: boolean) => void;
    closeOnOutsidePointerDown?: boolean;
    closeOnEscape?: boolean;
    /** Обновлять позицию на scroll/resize viewport. */
    updateOnViewportChange?: boolean;
}

/** Пропсы {@link ViewportLayer}. */
export interface ViewportLayerProps {
    open: boolean;
    className?: string;
    children?: React.ReactNode;
    onOpenChange?: (open: boolean) => void;
    closeOnEscape?: boolean;
}

function getPosition(anchor: HTMLElement, placement: FloatingLayerPlacement): FloatingLayerPosition {
    const rect = anchor.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom - FLOATING_LAYER_OFFSET - FLOATING_LAYER_VIEWPORT_PADDING;
    const spaceAbove = rect.top - FLOATING_LAYER_OFFSET - FLOATING_LAYER_VIEWPORT_PADDING;
    const resolvedPlacement = placement === "auto" ? (spaceBelow >= spaceAbove ? "bottom-start" : "top-start") : placement;
    const availableHeight = Math.max(0, resolvedPlacement === "bottom-start" ? spaceBelow : spaceAbove);

    switch (resolvedPlacement) {
        case "bottom-start":
            return {
                top: rect.bottom + FLOATING_LAYER_OFFSET,
                left: rect.left,
                width: rect.width,
                availableHeight,
                resolvedPlacement,
            };
        case "top-start":
            return {
                top: rect.top - FLOATING_LAYER_OFFSET,
                left: rect.left,
                width: rect.width,
                availableHeight,
                resolvedPlacement,
            };
    }
}

function useFloatingLayerContext(): FloatingLayerContextValue {
    return (
        React.useContext(FloatingLayerContext) ?? {
            portalContainer: document.body,
            baseZIndex: DEFAULT_FLOATING_LAYER_Z_INDEX,
        }
    );
}

function useOptionalBackHandler(handler: () => boolean): void {
    const { registerBackHandler } = useFloatingLayerContext();

    React.useEffect(() => {
        if (registerBackHandler == null) {
            return undefined;
        }

        return registerBackHandler(handler);
    }, [handler, registerBackHandler]);
}

/**
 * Рендерит transient UI-layer в общий portal.
 *
 * Используй для dropdown/popover/listbox внутри shared UI. Не используй для app windows,
 * dialogs или экранных форм: для них есть `@app/overlay` definitions и `useWindows/useDialogs`.
 */
export function FloatingLayer({
    anchorRef,
    open,
    placement = "auto",
    className,
    children,
    onOpenChange,
    closeOnOutsidePointerDown = true,
    closeOnEscape = true,
    updateOnViewportChange = true,
}: FloatingLayerProps) {
    const { portalContainer, baseZIndex } = useFloatingLayerContext();
    const layerRef = React.useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = React.useState<FloatingLayerPosition | null>(null);

    const updatePosition = React.useCallback(() => {
        const anchor = anchorRef.current;
        if (anchor == null) return;

        setPosition(getPosition(anchor, placement));
    }, [anchorRef, placement]);

    React.useLayoutEffect(() => {
        if (!open) return;

        updatePosition();
    }, [open, updatePosition]);

    React.useEffect(() => {
        if (!open) return;

        const handlePointerDown = (event: PointerEvent) => {
            if (!closeOnOutsidePointerDown) return;
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (anchorRef.current?.contains(target) === true || layerRef.current?.contains(target) === true) return;

            onOpenChange?.(false);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (!closeOnEscape || event.key !== "Escape") return;

            event.preventDefault();
            event.stopPropagation();
            onOpenChange?.(false);
        };

        const handleViewportChange = () => {
            if (updateOnViewportChange) {
                updatePosition();
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown, true);
        window.addEventListener("resize", handleViewportChange);
        window.addEventListener("scroll", handleViewportChange, true);

        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown, true);
            window.removeEventListener("resize", handleViewportChange);
            window.removeEventListener("scroll", handleViewportChange, true);
        };
    }, [anchorRef, closeOnEscape, closeOnOutsidePointerDown, onOpenChange, open, updateOnViewportChange, updatePosition]);

    const handleAppBack = React.useCallback((): boolean => {
        if (!open) {
            return false;
        }

        onOpenChange?.(false);
        return true;
    }, [onOpenChange, open]);

    useOptionalBackHandler(handleAppBack);

    if (!open) {
        return null;
    }

    const layerStyle: React.CSSProperties | FloatingLayerStyle =
        position != null
            ? {
                  position: "fixed",
                  zIndex: baseZIndex,
                  top: position.top,
                  left: position.left,
                  width: position.width,
                  transform: position.resolvedPlacement === "top-start" ? "translateY(-100%)" : undefined,
                  "--floating-layer-available-height": `${position.availableHeight}px`,
                  "--floating-layer-trigger-width": `${position.width}px`,
              }
            : {
                  position: "fixed",
                  zIndex: baseZIndex,
                  opacity: 0,
              };

    return createPortal(
        <div
            ref={layerRef}
            className={className}
            style={layerStyle}
            onKeyDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
        >
            {children}
        </div>,
        portalContainer,
    );
}

/**
 * Рендерит transient слой, занимающий весь viewport, в общий portal.
 * Используется для mobile picker/tray поверх текущего экрана без привязки к anchor geometry.
 */
export function ViewportLayer({ open, className, children, onOpenChange, closeOnEscape = true }: ViewportLayerProps) {
    const { portalContainer, baseZIndex } = useFloatingLayerContext();

    React.useEffect(() => {
        if (!open) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (!closeOnEscape || event.key !== "Escape") return;

            event.preventDefault();
            event.stopPropagation();
            onOpenChange?.(false);
        };

        document.addEventListener("keydown", handleKeyDown, true);

        return () => {
            document.removeEventListener("keydown", handleKeyDown, true);
        };
    }, [closeOnEscape, onOpenChange, open]);

    const handleAppBack = React.useCallback((): boolean => {
        if (!open) {
            return false;
        }

        onOpenChange?.(false);
        return true;
    }, [onOpenChange, open]);

    useOptionalBackHandler(handleAppBack);

    if (!open) {
        return null;
    }

    return createPortal(
        <div
            className={className}
            style={{
                position: "fixed",
                inset: 0,
                zIndex: baseZIndex,
            }}
            onKeyDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
        >
            {children}
        </div>,
        portalContainer,
    );
}
